import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import {
  VerifiedUser as VerifiedUserIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Base64FileUpload from '../components/Base64FileUpload';
import { 
  createVerificationRequest, 
  canCreateVerificationRequest,
  getUserVerificationRequests,
  CreateVerificationRequestData 
} from '../lib/api/verification';

const VerificationRequestPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [existingRequests, setExistingRequests] = useState<any[]>([]);
  
  const [formData, setFormData] = useState<CreateVerificationRequestData>({
    verification_photo_url: '',
    linkedin_url: '',
    website_url: '',
    portfolio_url: '',
  });

  useEffect(() => {
    checkEligibility();
    loadExistingRequests();
  }, []);

  const checkEligibility = async () => {
    try {
      const { canCreate: eligible, error } = await canCreateVerificationRequest();
      if (error) {
        setError(error);
        return;
      }
      setCanCreate(eligible);
    } catch (err) {
      console.error('Error checking eligibility:', err);
      setError('Грешка при проверка на възможността за верификация');
    }
  };

  const loadExistingRequests = async () => {
    try {
      const { data, error } = await getUserVerificationRequests();
      if (error) {
        console.error('Error loading existing requests:', error);
        return;
      }
      setExistingRequests(data);
    } catch (err) {
      console.error('Error loading existing requests:', err);
    }
  };

  const handleInputChange = (field: keyof CreateVerificationRequestData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleFileUpload = (base64Url: string) => {
    setFormData(prev => ({
      ...prev,
      verification_photo_url: base64Url
    }));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate URLs
      const urlPattern = /^https?:\/\/.+/;
      if (formData.linkedin_url && !urlPattern.test(formData.linkedin_url)) {
        setError('LinkedIn URL трябва да започва с http:// или https://');
        return;
      }
      if (formData.website_url && !urlPattern.test(formData.website_url)) {
        setError('Website URL трябва да започва с http:// или https://');
        return;
      }
      if (formData.portfolio_url && !urlPattern.test(formData.portfolio_url)) {
        setError('Portfolio URL трябва да започва с http:// или https://');
        return;
      }
    }
    setError(null);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!formData.verification_photo_url) {
      setError('Моля, качете снимка с кодовата фраза');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { success, data, error } = await createVerificationRequest(formData);
      
      if (error) {
        setError(error);
        return;
      }

      if (success && data) {
        setVerificationCode(data.verification_code);
        setSuccess(true);
        setActiveStep(2);
        loadExistingRequests();
      }
    } catch (err) {
      console.error('Error creating verification request:', err);
      setError('Неочаквана грешка при създаване на заявката');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'EXPIRED': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Чака одобрение';
      case 'APPROVED': return 'Одобрена';
      case 'REJECTED': return 'Отхвърлена';
      case 'EXPIRED': return 'Изтекла';
      default: return status;
    }
  };

  if (!canCreate && !error) {
    return (
      <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Card sx={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            overflow: 'hidden',
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <WarningIcon sx={{ fontSize: 64, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#ffffff', mb: 2 }}>
                Не можете да заявите верификация
              </Typography>
              <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3 }}>
                Само фриленсъри могат да заявяват верификация. Агенциите се считат автоматично за верифицирани след одобрение.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/profile')}
                sx={{
                  background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Към профила
              </Button>
            </CardContent>
          </Card>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#ffffff',
              mb: 2,
            }}
          >
            Заявка за верификация
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#a0a0a0',
            }}
          >
            Потвърдете самоличността си за по-голямо доверие
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Заявката за верификация е изпратена успешно! Ще получите отговор в рамките на 24 часа.
          </Alert>
        )}

        {/* Existing Requests */}
        {existingRequests.length > 0 && (
          <Card sx={{ mb: 4, background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                Вашите заявки за верификация
              </Typography>
              {existingRequests.map((request) => (
                <Box key={request.id} sx={{ mb: 2, p: 2, background: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      Заявка #{request.id.slice(0, 8)}
                    </Typography>
                    <Chip
                      label={getStatusText(request.status)}
                      color={getStatusColor(request.status) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#a0a0a0' }}>
                    Подадена на: {new Date(request.submitted_at).toLocaleDateString('bg-BG')}
                  </Typography>
                  {request.rejection_reason && (
                    <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 1 }}>
                      Причина за отхвърляне: {request.rejection_reason}
                    </Typography>
                  )}
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Verification Form */}
        {!success && (
          <Card sx={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            overflow: 'hidden',
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stepper activeStep={activeStep} orientation="horizontal">
                <Step>
                  <StepLabel>Профилни връзки</StepLabel>
                  <StepContent>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3 }}>
                        Споделете вашия LinkedIn профил, уебсайт или портфолио, където се вижда вашето име и снимка.
                      </Typography>
                      
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={t('verification.linkedinProfile')}
                            placeholder="https://linkedin.com/in/yourname"
                            value={formData.linkedin_url}
                            onChange={handleInputChange('linkedin_url')}
                            InputProps={{
                              startAdornment: <LinkIcon sx={{ mr: 1, color: '#a0a0a0' }} />
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover fieldset': { borderColor: '#28A0F0' },
                                '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                              },
                              '& .MuiInputLabel-root': { color: '#a0a0a0' },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={t('verification.website')}
                            placeholder="https://yourwebsite.com"
                            value={formData.website_url}
                            onChange={handleInputChange('website_url')}
                            InputProps={{
                              startAdornment: <LinkIcon sx={{ mr: 1, color: '#a0a0a0' }} />
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover fieldset': { borderColor: '#28A0F0' },
                                '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                              },
                              '& .MuiInputLabel-root': { color: '#a0a0a0' },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={t('verification.portfolio')}
                            placeholder="https://yourportfolio.com"
                            value={formData.portfolio_url}
                            onChange={handleInputChange('portfolio_url')}
                            InputProps={{
                              startAdornment: <LinkIcon sx={{ mr: 1, color: '#a0a0a0' }} />
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                color: '#ffffff',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover fieldset': { borderColor: '#28A0F0' },
                                '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                              },
                              '& .MuiInputLabel-root': { color: '#a0a0a0' },
                              '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </StepContent>
                </Step>
                
                <Step>
                  <StepLabel>Снимка с кодова фраза</StepLabel>
                  <StepContent>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3 }}>
                        Качете снимка, на която се вижда вашето лице и кодовата фраза, която ще получите.
                      </Typography>
                      
                      <Base64FileUpload
                        onFileUpload={handleFileUpload}
                        allowedTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp']}
                        maxSize={2} // 2MB
                        label={t('verification.uploadPhoto')}
                        description={t('verification.uploadPhotoDescription')}
                      />
                      
                      {formData.verification_photo_url && (
                        <Box sx={{ mt: 2, p: 2, background: 'rgba(40, 160, 240, 0.1)', borderRadius: 2, border: '1px solid rgba(40, 160, 240, 0.3)' }}>
                          <Typography variant="body2" sx={{ color: '#28A0F0', display: 'flex', alignItems: 'center' }}>
                            <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                            Снимката е качена успешно
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </StepContent>
                </Step>
                
                <Step>
                  <StepLabel>Потвърждение</StepLabel>
                  <StepContent>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3 }}>
                        Проверете данните и изпратете заявката за верификация.
                      </Typography>
                      
                      <Paper sx={{ p: 3, background: 'rgba(255, 255, 255, 0.05)', mb: 3 }}>
                        <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                          Резюме на заявката
                        </Typography>
                        <Box sx={{ height: 1, bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
                        
                        {formData.linkedin_url && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>LinkedIn:</Typography>
                            <Typography variant="body2" sx={{ color: '#ffffff' }}>{formData.linkedin_url}</Typography>
                          </Box>
                        )}
                        
                        {formData.website_url && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>Уебсайт:</Typography>
                            <Typography variant="body2" sx={{ color: '#ffffff' }}>{formData.website_url}</Typography>
                          </Box>
                        )}
                        
                        {formData.portfolio_url && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>Портфолио:</Typography>
                            <Typography variant="body2" sx={{ color: '#ffffff' }}>{formData.portfolio_url}</Typography>
                          </Box>
                        )}
                        
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>Снимка:</Typography>
                          <Typography variant="body2" sx={{ color: formData.verification_photo_url ? '#10b981' : '#ef4444' }}>
                            {formData.verification_photo_url ? 'Качена' : 'Не е качена'}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  </StepContent>
                </Step>
              </Stepper>

              {/* Navigation Buttons */}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ color: '#a0a0a0' }}
                >
                  Назад
                </Button>
                
                {activeStep < 2 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{
                      background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                      px: 4,
                      py: 1,
                      fontWeight: 600,
                    }}
                  >
                    Напред
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !formData.verification_photo_url}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <VerifiedUserIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      px: 4,
                      py: 1,
                      fontWeight: 600,
                    }}
                  >
                    {loading ? 'Изпращане...' : 'Изпрати заявката'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Success Message */}
        {success && verificationCode && (
          <Card sx={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 4,
            overflow: 'hidden',
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#ffffff', mb: 2 }}>
                Заявката е изпратена успешно!
              </Typography>
              <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3 }}>
                Вашата кодова фраза е: <strong style={{ color: '#10b981' }}>{verificationCode}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3 }}>
                Моля, напишете тази фраза на лист хартия и снимайте заедно с лицето си. 
                Снимката ще бъде проверена от нашия екип в рамките на 24 часа.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/profile')}
                sx={{
                  background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Към профила
              </Button>
            </CardContent>
          </Card>
        )}
      </Container>
      
      <Footer />
    </Box>
  );
};

export default VerificationRequestPage;
